import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import { createLoan } from "../../services/loans.service.ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getAllActivePartners,
  fetchPartner,
} from "../../services/partner.service.ts";
import S3Service from "../../services/s3.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import dayjs from "dayjs";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/es/locale/es_ES";
import "dayjs/locale/es";
import { getCurrencyFormat, parseCurrency } from "../../utils/index.ts";
dayjs.locale("es");

const getMonthEnd = (d: any) => dayjs(d).endOf("month").date();
const normalizeStartToFortnight = (d: any) => {
  const date = dayjs(d);
  const day = date.date();
  return day <= 15 ? date.startOf("month") : date.date(16);
};
const normalizeEndToFortnight = (d: any) => {
  const date = dayjs(d);
  const day = date.date();
  return day <= 15 ? date.date(15) : date.date(getMonthEnd(date));
};
const countFortnights = (start: any, end: any) => {
  let s = normalizeStartToFortnight(start);
  const e = normalizeEndToFortnight(end);
  if (s.isAfter(e, "day")) return 0;
  let count = 0;
  while (!s.isAfter(e, "day")) {
    const currentEnd = s.date() <= 15 ? s.date(15) : s.date(getMonthEnd(s));
    count += 1;
    s = currentEnd.add(1, "day");
    s = normalizeStartToFortnight(s);
  }
  return count;
};

const NewLoan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState<any[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<any>(null);
  const [profileUrl, setProfileUrl] = useState<string>("");
  const [requestAmountInput, setRequestAmountInput] = useState<string>("");
  const [isEditingRequestAmount, setIsEditingRequestAmount] =
    useState<boolean>(false);
  const [fortnightsCount, setFortnightsCount] = useState<number>(0);
  const [tempStartDate, setTempStartDate] = useState<any>(null);
  useAppSettings();

  const validationSchema = Yup.object().shape({
    partnerId: Yup.number().required("Colaborador requerido"),
    requestAmount: Yup.number()
      .positive()
      .required("Monto solicitado requerido")
      .test("salary-cap", "No puede exceder el salario mensual del colaborador", function (value) {
        const monthly = Number(selectedPartner?.candidate?.monthlySalary);
        if (!monthly || !value) return true;
        return Number(value) <= monthly;
      }),
    amountDiscount: Yup.number()
      .positive()
      .required("Monto de descuento requerido"),
    startDayPay: Yup.number().when("approved", {
      is: true,
      then: (schema) => schema.required("Fecha de inicio requerida"),
      otherwise: (schema) => schema.optional(),
    }),
    endDayPay: Yup.number().when("approved", {
      is: true,
      then: (schema) => schema.required("Fecha de fin requerida"),
      otherwise: (schema) => schema.optional(),
    }),
    approved: Yup.boolean().required("Debe seleccionar una decisión"),
    reviewerComment: Yup.string().when("approved", {
      is: false,
      then: (schema) =>
        schema.required("El comentario es obligatorio al rechazar"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    validationSchema,
    initialValues: {
      partnerId: "",
      requestAmount: "",
      amountDiscount: "",
      startDayPay: null,
      endDayPay: null,
      note: "",
      approved: false,
      reviewerComment: "",
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const created = await createLoan({
          partnerId: Number(values.partnerId),
          requestAmount: Number(values.requestAmount),
          amountDiscount: Number(values.amountDiscount),
          startDayPay: values.startDayPay || undefined,
          endDayPay: values.endDayPay || undefined,
          note: values.note || undefined,
          status: "pending",
          approved: values.approved,
          isRejected: !values.approved,
          reviewerComment: values.reviewerComment || undefined,
        });
        notification("Éxito", "Préstamo creado", "success");
        navigate("/prestamos");
      } catch (e: any) {
        notification("Error", e?.message || "No se pudo crear", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    getAllActivePartners()
      .then((res: any) => setPartners(res || []))
      .catch(() => setPartners([]));
  }, []);

  useEffect(() => {
    if (!isEditingRequestAmount) {
      const val = formik.values.requestAmount;
      if (val === null || val === undefined || val === "") {
        setRequestAmountInput("");
      } else {
        setRequestAmountInput(getCurrencyFormat(Number(val)));
      }
    }
  }, [formik.values.requestAmount, isEditingRequestAmount]);

  const displayCurrency = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (!isFinite(num)) return "";
    return getCurrencyFormat(num);
  };

  const recalcAmountDiscount = (totalStr: string | number, count: number) => {
    const total = Number(totalStr);
    if (!count || !isFinite(total) || total <= 0) {
      formik.setFieldValue("amountDiscount", "");
      return;
    }
    const perQuincena = total / count;
    formik.setFieldValue("amountDiscount", perQuincena.toFixed(2));
  };

  useEffect(() => {
    recalcAmountDiscount(formik.values.requestAmount, fortnightsCount);
  }, [formik.values.requestAmount, fortnightsCount]);

  const loadPartnerInfo = async (partnerId: number) => {
    try {
      setLoading(true);
      const partner = await fetchPartner(partnerId);
      setSelectedPartner(partner);
      const curp = partner?.person?.curp;
      if (curp) {
        try {
          const file = await S3Service.getFile("PERFIL_" + curp);
          setProfileUrl(file?.url || "");
        } catch {
          setProfileUrl("");
        }
      } else {
        setProfileUrl("");
      }
    } catch (error) {
      setSelectedPartner(null);
      setProfileUrl("");
    } finally {
      setLoading(false);
    }
  };

  const errorFor = (name: string) =>
    formik.touched[name as any] && formik.errors[name as any] ? (
      <div className="invalid-feedback" style={{ display: "block" }}>
        {formik.errors[name as any] as any}
      </div>
    ) : null;

  const handlePeriodDateChange = (range: any) => {
    const [start, end] = range || [];
    if (!start || !end) {
      setFortnightsCount(0);
      formik.setFieldValue("startDayPay", null);
      formik.setFieldValue("endDayPay", null);
      formik.setFieldValue("amountDiscount", "");
      formik.setFieldError("startDayPay", undefined);
      formik.setFieldError("endDayPay", undefined);
      return;
    }
    const ns = normalizeStartToFortnight(start);
    const ne = normalizeEndToFortnight(end);
    const count = countFortnights(ns, ne);

    setFortnightsCount(count);
    formik.setFieldValue("startDayPay", ns.unix());
    formik.setFieldValue("endDayPay", ne.unix());
    formik.setFieldError("startDayPay", undefined);
    formik.setFieldError("endDayPay", undefined);

    const currentAmount = Number(formik.values.requestAmount);
    if (currentAmount > 0 && count > 0) {
      const perQuincena = currentAmount / count;
      formik.setFieldValue("amountDiscount", perQuincena.toFixed(2));
    } else {
      formik.setFieldValue("amountDiscount", "");
    }
  };

  const disabledDateForPeriod = (_current: any) => {
    return false;
  };

  const handleCalendarChange = (dates: any) => {
    if (dates && dates[0]) {
      setTempStartDate(dates[0]);
    } else {
      setTempStartDate(null);
    }
  };

  const handlePartnerChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    formik.handleChange(e);
    const value = e.target.value;
    if (value) await loadPartnerInfo(Number(value));
  };

  return (
    <Layout>
      <div>
        <Title
          baseTitle="Préstamos"
          basePath="/prestamos"
          title="Nuevo préstamo"
        />

        <div className="d-flex flex-column flex-lg-row gap-4 m-4">
          <div className="flex-lg-grow-0" style={{ width: "30%" }}>
            <div className="card border-0 rounded h-100 shadow-sm">
              <div className="card-body text-center">
                <h5 className="mb-3 text-azul">Información del colaborador</h5>
                {loading ? (
                  <div className="d-flex flex-column align-items-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <div className="text-muted small mt-2">
                      Cargando colaborador...
                    </div>
                  </div>
                ) : selectedPartner ? (
                  <>
                    <div className="mb-3">
                      <img
                        src={profileUrl || "/assets/img/avatar.jpg"}
                        alt="Foto de perfil"
                        className="rounded-circle shadow"
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          border: "4px solid #f8f9fa",
                        }}
                        onError={(e: any) => {
                          e.target.src = "/assets/img/avatar.jpg";
                        }}
                      />
                    </div>
                    <div className="text-center">
                      <h6 className="mb-1 fw-bold fs-4 text-dark">
                        {toCamelCase(
                          `${selectedPartner?.person?.firstName || ""} ${
                            selectedPartner?.person?.lastName || ""
                          }`
                        )}
                      </h6>
                      <div className="text-muted small mb-1">Puesto</div>
                      <div className="fw-semibold fs-6 text-dark">
                        {selectedPartner?.candidate?.vacancy?.position?.name ||
                          "-"}
                      </div>
                      <div className="text-muted small mt-3 mb-1">Sucursal</div>
                      <div className="fw-semibold fs-6 text-dark">
                        {selectedPartner?.candidate?.branch?.name || "-"}
                      </div>
                    </div>
                    <div className="alert alert-info mt-3 text-center">
                      <i className="fa fa-info-circle"></i> Se registrará el
                      préstamo para este colaborador
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <img
                        src={"/assets/img/avatar.jpg"}
                        alt="Foto de perfil"
                        className="rounded-circle shadow"
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          border: "4px solid #f8f9fa",
                        }}
                      />
                    </div>
                    <div className="text-center text-muted small mb-3">
                      Selecciona un colaborador para ver su información
                    </div>
                    <div className="alert alert-info mb-2 text-start">
                      Rellena los campos para crear un préstamo
                    </div>
                    <div className="alert alert-warning mb-0 text-start">
                      El monto por quincena se calculará automáticamente
                      dividiendo el monto solicitado entre el número de
                      quincenas
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex-lg-grow-0" style={{ width: "70%" }}>
            <div className="card border-0 rounded mb-4">
              <Panel>
                <PanelHeader noButton={true} className="bg-azul">
                  Datos del préstamo
                </PanelHeader>
                <div className="p-4">
                  <ConfigProvider locale={esES}>
                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <label className="form-label">Colaborador</label>
                        <select
                          className="form-select"
                          name="partnerId"
                          value={formik.values.partnerId}
                          onChange={handlePartnerChange}
                          onBlur={formik.handleBlur}
                        >
                          <option value="">Selecciona un colaborador</option>
                          {partners?.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              {p.person
                                ? `${p.person.firstName} ${p.person.lastName}`
                                : p.id}
                            </option>
                          ))}
                        </select>
                        {errorFor("partnerId")}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Monto solicitado</label>
                        <input
                          type="text"
                          className="form-control"
                          name="requestAmount"
                          value={
                            isEditingRequestAmount
                              ? requestAmountInput
                              : displayCurrency(formik.values.requestAmount)
                          }
                          disabled={formik.values.approved === false}
                          onFocus={() => {
                            if (formik.values.approved === false) return;
                            setIsEditingRequestAmount(true);
                            const current = formik.values.requestAmount;
                            setRequestAmountInput(
                              current === null ||
                                current === undefined ||
                                current === ""
                                ? ""
                                : String(current)
                            );
                          }}
                          onChange={(e) => {
                            if (formik.values.approved === false) return;
                            const val = e.target.value;
                            setRequestAmountInput(val);
                            const parsed = parseCurrency(val || "");
                            if (isNaN(parsed)) {
                              formik.setFieldValue("requestAmount", "");
                            } else {
                              formik.setFieldValue("requestAmount", parsed);
                            }
                          }}
                          onBlur={(e) => {
                            formik.handleBlur(e);
                            setIsEditingRequestAmount(false);
                            const parsed = parseCurrency(
                              requestAmountInput || ""
                            );
                            if (isNaN(parsed)) {
                              setRequestAmountInput("");
                              formik.setFieldValue("requestAmount", "");
                            } else {
                              setRequestAmountInput(getCurrencyFormat(parsed));
                              formik.setFieldValue("requestAmount", parsed);
                            }
                          }}
                        />
                        {errorFor("requestAmount")}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Monto por quincena</label>
                        <input
                          type="text"
                          className="form-control"
                          name="amountDiscount"
                          value={displayCurrency(formik.values.amountDiscount)}
                          disabled
                        />
                        {errorFor("amountDiscount")}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Quincenas</label>
                        <input
                          type="number"
                          className="form-control"
                          value={fortnightsCount || 0}
                          disabled
                        />
                      </div>
                      <div className="col-md-12 mb-3">
                        <label className="form-label">
                          Periodo de descuento
                        </label>
                        <DatePicker.RangePicker
                          style={{ width: "100%" }}
                          format="DD/MM/YYYY"
                          disabled={formik.values.approved === false}
                          disabledDate={disabledDateForPeriod}
                          onCalendarChange={handleCalendarChange}
                          value={
                            formik.values.startDayPay && formik.values.endDayPay
                              ? [
                                  dayjs.unix(formik.values.startDayPay),
                                  dayjs.unix(formik.values.endDayPay),
                                ]
                              : null
                          }
                          renderExtraFooter={() => (
                            <div className="text-muted small">
                              <i className="fa fa-info-circle"></i> Selecciona
                              las fechas del periodo de descuento
                            </div>
                          )}
                          onChange={(dates) => {
                            setTempStartDate(null);
                            handlePeriodDateChange(dates);
                          }}
                        />
                        {errorFor("startDayPay")}
                        {errorFor("endDayPay")}
                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label">Decisión *</label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="true"
                              className="form-check-input"
                              id="approve"
                              checked={formik.values.approved === true}
                              onChange={() =>
                                formik.setFieldValue("approved", true)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="approve"
                            >
                              Aprobar
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="false"
                              className="form-check-input"
                              id="reject"
                              checked={formik.values.approved === false}
                              onChange={() =>
                                formik.setFieldValue("approved", false)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="reject"
                            >
                              Rechazar
                            </label>
                          </div>
                        </div>
                        {formik.touched.approved && formik.errors.approved && (
                          <div className="text-danger small mt-1">
                            {formik.errors.approved}
                          </div>
                        )}
                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label">
                          Comentario del revisor{" "}
                          {formik.values.approved === false && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                        <textarea
                          className={`form-control ${
                            formik.touched.reviewerComment &&
                            formik.errors.reviewerComment
                              ? "is-invalid"
                              : ""
                          }`}
                          rows={3}
                          placeholder="Comentario del revisor"
                          value={formik.values.reviewerComment}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "reviewerComment",
                              e.target.value
                            )
                          }
                          onBlur={formik.handleBlur}
                          name="reviewerComment"
                        />
                        {formik.touched.reviewerComment &&
                          formik.errors.reviewerComment && (
                            <div className="invalid-feedback">
                              {formik.errors.reviewerComment}
                            </div>
                          )}
                      </div>
                    </div>
                  </ConfigProvider>
                </div>
              </Panel>
            </div>
          </div>
        </div>

        <div className="card border-0 m-4 rounded p-3">
          <div className="row justify-content-end">
            <div className="col-md-3">
              <Button
                onClick={() => navigate(-1)}
                title="Cancelar"
                variant="secondary"
                type="button"
                disabled={loading}
              />
            </div>
            <div className="col-md-3">
              <Button
                onClick={() => formik.handleSubmit()}
                title="Guardar préstamo"
                variant="azul"
                disabled={loading}
                type="submit"
              />
            </div>
          </div>
        </div>
        <Loader isLoading={loading} />
      </div>
    </Layout>
  );
};

export default NewLoan;
